import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../lib/api";

interface OrgUser {
  id: string;
  email: string;
  role: string;
  first_name: string | null;
  last_name: string | null;
  is_active: boolean;
  last_login_at: string | null;
}

interface Organization {
  id: string;
  name: string;
  plan: string;
}

interface UserRow extends OrgUser {
  org_id: string;
  org_name: string;
  org_plan: string;
}

const PLAN_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  core:  { bg: "#3b82f620", color: "#3b82f6", label: "Core"  },
  pro:   { bg: "#8b5cf620", color: "#8b5cf6", label: "Pro"   },
  ultra: { bg: "#f59e0b20", color: "#f59e0b", label: "Ultra" },
};

const ROLE_STYLE: Record<string, { bg: string; color: string }> = {
  superadmin: { bg: "#ef444420", color: "#ef4444" },
  admin:      { bg: "#0891b220", color: "#0891b2" },
  user:       { bg: "#6b728018", color: "#6b7280" },
  readonly:   { bg: "#6b728018", color: "#6b7280" },
};

const card: React.CSSProperties = {
  background: "var(--clr-surface)",
  backdropFilter: "var(--clr-blur)",
  WebkitBackdropFilter: "var(--clr-blur)",
  border: "1px solid var(--clr-border)",
  borderRadius: 18,
  boxShadow: "var(--clr-shadow-sm)",
  padding: "1.25rem 1.5rem",
};

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "8px 12px", borderRadius: 12,
  border: "1px solid var(--clr-border)", background: "var(--clr-input-bg)",
  color: "var(--clr-text)", fontSize: "0.875rem", boxSizing: "border-box", outline: "none",
};

const labelStyle: React.CSSProperties = {
  fontSize: "0.76rem", color: "var(--clr-text3)", fontWeight: 600, display: "block", marginBottom: 4,
};

function PlanBadge({ plan }: { plan: string }) {
  const s = PLAN_STYLE[plan] ?? { bg: "#6b728020", color: "#6b7280", label: plan };
  return (
    <span style={{ background: s.bg, color: s.color, padding: "2px 10px", borderRadius: 20, fontSize: "0.73rem", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase" }}>
      {s.label}
    </span>
  );
}

function RoleBadge({ role }: { role: string }) {
  const s = ROLE_STYLE[role] ?? { bg: "#6b728018", color: "#6b7280" };
  return (
    <span style={{ background: s.bg, color: s.color, padding: "2px 8px", borderRadius: 20, fontSize: "0.73rem", fontWeight: 600 }}>
      {role}
    </span>
  );
}

function StatusDot({ active }: { active: boolean }) {
  const color = active ? "#22c55e" : "#ef4444";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      background: color + "1e", color,
      borderRadius: 999, padding: "3px 11px",
      fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap",
    }}>
      {active ? "Activo" : "Inactivo"}
    </span>
  );
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
}

// ── SVG icons ─────────────────────────────────────────────────────────────────

const IconEdit = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const IconKey = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="8" cy="15" r="5"/><path d="M21 2l-9.3 9.3M15.5 7.5l3 3"/>
  </svg>
);
const IconToggle = ({ active }: { active: boolean }) => active ? (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><path d="M4.93 4.93l14.14 14.14"/>
  </svg>
) : (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="M22 4L12 14.01l-3-3"/>
  </svg>
);
const IconTrash = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

// ── Actions menu ──────────────────────────────────────────────────────────────

interface ActionsMenuProps {
  user: UserRow;
  onEdit: () => void;
  onResetPassword: () => void;
  onToggle: () => void;
  onSubstitute: () => void;
  onDelete: () => void;
}

function ActionsMenu({ user, onEdit, onResetPassword, onToggle, onSubstitute, onDelete }: ActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      const t = e.target as Node;
      if (
        menuRef.current && !menuRef.current.contains(t) &&
        btnRef.current  && !btnRef.current.contains(t)
      ) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  function handleOpen() {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const MENU_H = 236;
      const openUpward = rect.bottom + MENU_H > window.innerHeight;
      setPos({
        top: openUpward ? rect.top - MENU_H - 4 : rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    setOpen(v => !v);
  }

  const item = (
    label: string,
    icon: React.ReactNode,
    onClick: () => void,
    danger = false,
  ) => (
    <button
      onClick={() => { setOpen(false); onClick(); }}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 9,
        padding: "0.55rem 0.875rem", background: "none", border: "none",
        cursor: "pointer", textAlign: "left", fontSize: "0.82rem",
        color: danger ? "#ef4444" : "var(--clr-text)",
        transition: "background 0.1s",
        borderRadius: 8,
      }}
      onMouseEnter={e => (e.currentTarget.style.background = danger ? "rgba(239,68,68,0.08)" : "var(--clr-hover)")}
      onMouseLeave={e => (e.currentTarget.style.background = "none")}
    >
      <span style={{ opacity: danger ? 0.9 : 0.6, display: "flex" }}>{icon}</span>
      {label}
    </button>
  );

  const dropdown = open ? createPortal(
    <div
      ref={menuRef}
      style={{
        position: "fixed",
        top: pos.top,
        right: pos.right,
        background: "var(--clr-modal-solid)",
        border: "1px solid var(--clr-border)",
        borderRadius: 12,
        boxShadow: "0 8px 28px rgba(0,0,0,0.18)",
        zIndex: 9999,
        minWidth: 210,
        padding: "0.3rem",
      }}
    >
      {item("Editar usuario", <IconEdit />, onEdit)}
      {item("Restablecer contraseña", <IconKey />, onResetPassword)}
      <div style={{ height: 1, background: "var(--clr-border2)", margin: "0.25rem 0.5rem" }} />
      {item(
        user.is_active ? "Desactivar" : "Activar",
        <IconToggle active={user.is_active} />,
        onToggle,
      )}
      {/* Contrato con Italfarmaco, punto 10: cuentas nominativas; se sustituye a la persona */}
      {user.is_active && user.org_id && item("Sustituir por otra persona", <IconEdit />, onSubstitute)}
      <div style={{ height: 1, background: "var(--clr-border2)", margin: "0.25rem 0.5rem" }} />
      {item("Eliminar usuario", <IconTrash />, onDelete, true)}
    </div>,
    document.body
  ) : null;

  return (
    <div style={{ display: "inline-block" }}>
      <button
        ref={btnRef}
        onClick={handleOpen}
        title="Acciones"
        style={{
          width: 32, height: 32, borderRadius: 12,
          border: "1px solid var(--clr-border)",
          background: open ? "var(--clr-hover)" : "transparent",
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          color: "var(--clr-text3)", fontSize: "1.1rem", lineHeight: 1,
          transition: "all 0.1s",
        }}
        onMouseEnter={e => { if (!open) (e.currentTarget.style.background = "var(--clr-hover)"); }}
        onMouseLeave={e => { if (!open) (e.currentTarget.style.background = "transparent"); }}
      >
        ···
      </button>
      {dropdown}
    </div>
  );
}

// ── Edit user modal ───────────────────────────────────────────────────────────

interface EditModalProps {
  user: UserRow;
  orgs: Organization[];
  onClose: () => void;
  onSaved: (msg: string) => void;
}

function EditUserModal({ user, orgs, onClose, onSaved }: EditModalProps) {
  const [form, setForm] = useState({
    first_name: user.first_name ?? "",
    last_name:  user.last_name  ?? "",
    email:      user.email,
    role:       user.role,
    org_id:     user.org_id,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const field = (key: keyof typeof form) => ({
    value: form[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm(f => ({ ...f, [key]: e.target.value })),
  });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await api.put(`/users/${user.id}`, {
        first_name:      form.first_name || null,
        last_name:       form.last_name  || null,
        email:           form.email,
        role:            form.role,
        organization_id: form.org_id || null,
      });
      onSaved("Usuario actualizado correctamente");
      onClose();
    } catch (err: unknown) {
      setError((err as { message?: string })?.message ?? "Error al guardar");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(11,18,21,0.4)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: "var(--clr-modal-solid)", border: "1px solid var(--clr-border)", borderRadius: 22, padding: "1.75rem", width: "100%", maxWidth: 480, boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 700 }}>Editar usuario</h2>
            <p style={{ margin: "2px 0 0", fontSize: "0.78rem", color: "var(--clr-text3)" }}>{user.email}</p>
          </div>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 15, background: "var(--clr-modal-solid2)", border: "1px solid var(--clr-border2)", color: "var(--clr-text3)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.8rem", lineHeight: 1, flexShrink: 0 }}>✕</button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={labelStyle}>Nombre</label>
              <input style={inputStyle} placeholder="Nombre" {...field("first_name")} />
            </div>
            <div>
              <label style={labelStyle}>Apellidos</label>
              <input style={inputStyle} placeholder="Apellidos" {...field("last_name")} />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Email</label>
            <input style={inputStyle} type="email" required {...field("email")} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={labelStyle}>Rol</label>
              <select style={{ ...inputStyle, cursor: "pointer" }} {...field("role")}>
                <option value="admin">Admin</option>
                <option value="analyst">Analista</option>
                <option value="readonly">Solo lectura</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Organización</label>
              <select style={{ ...inputStyle, cursor: "pointer" }} {...field("org_id")}>
                <option value="">Sin organización</option>
                {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
          </div>

          {error && (
            <div style={{ color: "#ef4444", fontSize: "0.82rem" }}>{error}</div>
          )}

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
            <button type="button" onClick={onClose} style={{ padding: "8px 20px", borderRadius: 999, border: "1px solid var(--clr-border)", background: "transparent", color: "var(--clr-text3)", cursor: "pointer", fontSize: "0.875rem" }}>
              Cancelar
            </button>
            <button type="submit" disabled={saving} style={{ padding: "8px 20px", borderRadius: 999, border: "none", background: "var(--clr-org-primary)", color: "#fff", cursor: saving ? "default" : "pointer", fontSize: "0.875rem", fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
              {saving ? "Guardando…" : "Guardar cambios"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Reset password modal ──────────────────────────────────────────────────────

interface ResetPwdModalProps {
  user: UserRow;
  onClose: () => void;
  onSaved: (msg: string) => void;
}

function ResetPasswordModal({ user, onClose, onSaved }: ResetPwdModalProps) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("La contraseña debe tener al menos 8 caracteres"); return; }
    if (password !== confirm) { setError("Las contraseñas no coinciden"); return; }
    setSaving(true);
    try {
      await api.put(`/users/${user.id}`, { password });
      onSaved("Contraseña restablecida correctamente");
      onClose();
    } catch (err: unknown) {
      setError((err as { message?: string })?.message ?? "Error al restablecer contraseña");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(11,18,21,0.4)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: "var(--clr-modal-solid)", border: "1px solid var(--clr-border)", borderRadius: 22, padding: "1.75rem", width: "100%", maxWidth: 400, boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 700 }}>Restablecer contraseña</h2>
            <p style={{ margin: "2px 0 0", fontSize: "0.78rem", color: "var(--clr-text3)" }}>
              {[user.first_name, user.last_name].filter(Boolean).join(" ") || user.email}
            </p>
          </div>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 15, background: "var(--clr-modal-solid2)", border: "1px solid var(--clr-border2)", color: "var(--clr-text3)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.8rem", lineHeight: 1, flexShrink: 0 }}>✕</button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={labelStyle}>Nueva contraseña</label>
            <input style={inputStyle} type="password" placeholder="Mín. 8 caracteres" value={password} onChange={e => setPassword(e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Confirmar contraseña</label>
            <input style={inputStyle} type="password" placeholder="Repite la contraseña" value={confirm} onChange={e => setConfirm(e.target.value)} />
          </div>

          {error && (
            <div style={{ color: "#ef4444", fontSize: "0.82rem" }}>{error}</div>
          )}

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
            <button type="button" onClick={onClose} style={{ padding: "8px 20px", borderRadius: 999, border: "1px solid var(--clr-border)", background: "transparent", color: "var(--clr-text3)", cursor: "pointer", fontSize: "0.875rem" }}>
              Cancelar
            </button>
            <button type="submit" disabled={saving} style={{ padding: "8px 20px", borderRadius: 999, border: "none", background: "var(--clr-org-primary)", color: "#fff", cursor: saving ? "default" : "pointer", fontSize: "0.875rem", fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
              {saving ? "Guardando…" : "Restablecer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Create user modal ─────────────────────────────────────────────────────────

interface CreateModalProps {
  orgs: Organization[];
  onClose: () => void;
  onCreated: () => void;
}

function CreateUserModal({ orgs, onClose, onCreated }: CreateModalProps) {
  const [form, setForm] = useState({
    email: "", password: "", first_name: "", last_name: "",
    role: "admin", organization_id: orgs[0]?.id ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const field = (key: keyof typeof form) => ({
    value: form[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm(f => ({ ...f, [key]: e.target.value })),
  });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!form.email || !form.password) { setError("Email y contraseña son obligatorios"); return; }
    setSaving(true);
    try {
      await api.post("/users", {
        email: form.email, password: form.password,
        first_name: form.first_name || null, last_name: form.last_name || null,
        role: form.role, organization_id: form.organization_id || null,
      });
      onCreated();
      onClose();
    } catch (err: unknown) {
      setError((err as { message?: string })?.message ?? "Error al crear usuario");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(11,18,21,0.4)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "var(--clr-modal-solid)", border: "1px solid var(--clr-border)", borderRadius: 22, padding: "2rem", width: "100%", maxWidth: 460, boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
          <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 700 }}>Nuevo usuario</h2>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 15, background: "var(--clr-modal-solid2)", border: "1px solid var(--clr-border2)", color: "var(--clr-text3)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.8rem", lineHeight: 1, flexShrink: 0 }}>✕</button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={labelStyle}>Nombre</label>
              <input style={inputStyle} placeholder="Nombre" {...field("first_name")} />
            </div>
            <div>
              <label style={labelStyle}>Apellidos</label>
              <input style={inputStyle} placeholder="Apellidos" {...field("last_name")} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Email *</label>
            <input style={inputStyle} type="email" placeholder="usuario@empresa.com" required {...field("email")} />
          </div>
          <div>
            <label style={labelStyle}>Contraseña temporal *</label>
            <input style={inputStyle} type="password" placeholder="Mín. 8 caracteres" required {...field("password")} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={labelStyle}>Rol</label>
              <select style={{ ...inputStyle, cursor: "pointer" }} {...field("role")}>
                <option value="admin">Admin</option>
                <option value="analyst">Analista</option>
                <option value="readonly">Solo lectura</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Organización</label>
              <select style={{ ...inputStyle, cursor: "pointer" }} {...field("organization_id")}>
                <option value="">Sin organización</option>
                {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
          </div>

          {error && (
            <div style={{ color: "#ef4444", fontSize: "0.82rem" }}>{error}</div>
          )}

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
            <button type="button" onClick={onClose} style={{ padding: "8px 20px", borderRadius: 999, border: "1px solid var(--clr-border)", background: "transparent", color: "var(--clr-text3)", cursor: "pointer", fontSize: "0.875rem" }}>Cancelar</button>
            <button type="submit" disabled={saving} style={{ padding: "8px 20px", borderRadius: 999, border: "none", background: "var(--clr-org-primary)", color: "#fff", cursor: saving ? "default" : "pointer", fontSize: "0.875rem", fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
              {saving ? "Creando…" : "Crear usuario"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Substitute user modal ─────────────────────────────────────────────────────
// Sustitución de titular (contrato con Italfarmaco, punto 10): se da de baja a la persona y se
// crea la nueva con el mismo rol, idioma, preferencias de aviso e histórico, en una operación.

function SubstituteUserModal({ user, onClose, onDone }: { user: UserRow; onClose: () => void; onDone: (msg: string) => void }) {
  const [form, setForm] = useState({ email: "", password: "", first_name: "", last_name: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const field = (key: keyof typeof form) => ({
    value: form[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [key]: e.target.value })),
  });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!form.email || !form.password) { setError("Email y contraseña son obligatorios"); return; }
    setSaving(true);
    try {
      await api.post(`/users/${user.id}/substitute`, {
        email: form.email, password: form.password,
        first_name: form.first_name || null, last_name: form.last_name || null,
      });
      onDone(`${user.email} sustituido por ${form.email}`);
      onClose();
    } catch (err: unknown) {
      setError((err as { message?: string })?.message ?? "Error al sustituir el usuario");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(11,18,21,0.4)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "var(--clr-modal-solid)", border: "1px solid var(--clr-border)", borderRadius: 22, padding: "2rem", width: "100%", maxWidth: 460, boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
          <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 700 }}>Sustituir a {user.first_name || user.email}</h2>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 15, background: "var(--clr-modal-solid2)", border: "1px solid var(--clr-border2)", color: "var(--clr-text3)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.8rem", lineHeight: 1, flexShrink: 0 }}>✕</button>
        </div>
        <p style={{ margin: "0 0 1.25rem", fontSize: "0.82rem", color: "var(--clr-text3)", lineHeight: 1.5 }}>
          {user.email} ({user.org_name}) queda dada de baja y sus sesiones se cierran. La persona nueva hereda su rol,
          su idioma, sus preferencias de aviso y su histórico. No ocupa una plaza más.
        </p>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={labelStyle}>Nombre</label>
              <input style={inputStyle} placeholder="Nombre" {...field("first_name")} />
            </div>
            <div>
              <label style={labelStyle}>Apellidos</label>
              <input style={inputStyle} placeholder="Apellidos" {...field("last_name")} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Email de la persona nueva *</label>
            <input style={inputStyle} type="email" placeholder="usuario@empresa.com" required {...field("email")} />
          </div>
          <div>
            <label style={labelStyle}>Contraseña temporal *</label>
            <input style={inputStyle} type="password" placeholder="Mín. 8 caracteres" required {...field("password")} />
          </div>
          {error && <div style={{ color: "#ef4444", fontSize: "0.82rem" }}>{error}</div>}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
            <button type="button" onClick={onClose} style={{ padding: "8px 20px", borderRadius: 999, border: "1px solid var(--clr-border)", background: "transparent", color: "var(--clr-text3)", cursor: "pointer", fontSize: "0.875rem" }}>Cancelar</button>
            <button type="submit" disabled={saving} style={{ padding: "8px 20px", borderRadius: 999, border: "none", background: "var(--clr-org-primary)", color: "#fff", cursor: saving ? "default" : "pointer", fontSize: "0.875rem", fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
              {saving ? "Sustituyendo…" : "Sustituir"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Confirm delete modal ──────────────────────────────────────────────────────

function ConfirmDeleteModal({ user, onClose, onConfirm }: { user: UserRow; onClose: () => void; onConfirm: () => void }) {
  const [deleting, setDeleting] = useState(false);
  const name = [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email;

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(11,18,21,0.4)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: "var(--clr-modal-solid)", border: "1px solid var(--clr-border)", borderRadius: 22, padding: "1.75rem", width: "100%", maxWidth: 400, boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ textAlign: "center", marginBottom: "1.25rem" }}>
          <div style={{ width: 48, height: 48, borderRadius: "50%", background: "var(--clr-modal-solid2)", border: "1px solid var(--clr-border2)", color: "#ef4444", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1rem" }}>
            <IconTrash />
          </div>
          <h2 style={{ margin: "0 0 6px", fontSize: "1.05rem", fontWeight: 700 }}>Eliminar usuario</h2>
          <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--clr-text3)", lineHeight: 1.5 }}>
            ¿Eliminar a <strong style={{ color: "var(--clr-text)" }}>{name}</strong>?<br/>
            Esta acción no se puede deshacer.
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
          <button onClick={onClose} style={{ padding: "8px 22px", borderRadius: 999, border: "1px solid var(--clr-border)", background: "transparent", color: "var(--clr-text3)", cursor: "pointer", fontSize: "0.875rem" }}>
            Cancelar
          </button>
          <button
            onClick={async () => { setDeleting(true); onConfirm(); }}
            disabled={deleting}
            style={{ padding: "8px 22px", borderRadius: 999, border: "none", background: "#ef4444", color: "#fff", cursor: deleting ? "default" : "pointer", fontSize: "0.875rem", fontWeight: 600, opacity: deleting ? 0.7 : 1 }}
          >
            {deleting ? "Eliminando…" : "Sí, eliminar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────

type ModalState =
  | { type: "none" }
  | { type: "create" }
  | { type: "edit";          user: UserRow }
  | { type: "resetPassword"; user: UserRow }
  | { type: "substitute";    user: UserRow }
  | { type: "delete";        user: UserRow };

export default function SuperAdminUsuariosPage() {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState<ModalState>({ type: "none" });
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  function showToast(msg: string, ok = true) {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  }

  async function loadData() {
    setLoading(true); setError("");
    try {
      const orgList = await api.get<Organization[]>("/organizations");
      setOrgs(orgList);
      const results = await Promise.all(
        orgList.map(org =>
          api.get<OrgUser[]>(`/organizations/${org.id}/users`)
            .then(us => us.map(u => ({ ...u, org_id: org.id, org_name: org.name, org_plan: org.plan })))
            .catch(() => [] as UserRow[])
        )
      );
      setUsers(results.flat());
    } catch (e: unknown) {
      setError((e as { message?: string })?.message || "Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(); }, []);

  async function toggleActive(u: UserRow) {
    try {
      await api.put(`/users/${u.id}`, { is_active: !u.is_active });
      showToast(`Usuario ${u.is_active ? "desactivado" : "activado"}`);
      loadData();
    } catch (err: unknown) {
      // 409: la organización ya tiene todas sus cuentas activas (contrato con Italfarmaco, punto 10)
      showToast((err as { message?: string })?.message ?? "Error al actualizar usuario", false);
    }
  }

  async function deleteUser(u: UserRow) {
    try {
      await api.delete(`/users/${u.id}`);
      showToast("Usuario eliminado");
      setModal({ type: "none" });
      loadData();
    } catch { showToast("Error al eliminar usuario", false); setModal({ type: "none" }); }
  }

  const filtered = users.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    const name = [u.first_name, u.last_name].filter(Boolean).join(" ").toLowerCase();
    return u.email.toLowerCase().includes(q) || name.includes(q) ||
      u.org_name.toLowerCase().includes(q) || u.role.toLowerCase().includes(q);
  });

  const thCell: React.CSSProperties = {
    padding: "0.6rem 0.875rem", fontSize: "0.65rem", fontWeight: 600,
    textTransform: "uppercase", letterSpacing: 0.9, color: "var(--clr-text4)",
    borderBottom: "1px solid var(--clr-border2)", whiteSpace: "nowrap", textAlign: "left",
  };
  const tdCell: React.CSSProperties = {
    padding: "0.7rem 0.875rem", fontSize: "0.84rem", color: "var(--clr-text)",
    borderBottom: "1px solid var(--clr-border2)", verticalAlign: "middle",
  };

  return (
    <div style={{ padding: "1.75rem 2rem", maxWidth: 1100, margin: "0 auto", fontVariantNumeric: "tabular-nums" }}>
      {/* Modals */}
      {modal.type === "create" && (
        <CreateUserModal
          orgs={orgs}
          onClose={() => setModal({ type: "none" })}
          onCreated={() => { loadData(); showToast("Usuario creado correctamente"); }}
        />
      )}
      {modal.type === "edit" && (
        <EditUserModal
          user={modal.user}
          orgs={orgs}
          onClose={() => setModal({ type: "none" })}
          onSaved={msg => { loadData(); showToast(msg); }}
        />
      )}
      {modal.type === "resetPassword" && (
        <ResetPasswordModal
          user={modal.user}
          onClose={() => setModal({ type: "none" })}
          onSaved={msg => { showToast(msg); }}
        />
      )}
      {modal.type === "substitute" && (
        <SubstituteUserModal
          user={modal.user}
          onClose={() => setModal({ type: "none" })}
          onDone={msg => { loadData(); showToast(msg); }}
        />
      )}
      {modal.type === "delete" && (
        <ConfirmDeleteModal
          user={modal.user}
          onClose={() => setModal({ type: "none" })}
          onConfirm={() => deleteUser(modal.user)}
        />
      )}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.5rem", gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)" }}>
            Usuarios
            {!loading && (
              <span style={{ fontSize: "1rem", fontWeight: 400, color: "var(--clr-text3)", marginLeft: 10 }}>
                · {users.length} usuarios en {orgs.length} organizaciones
              </span>
            )}
          </h1>
          <p style={{ margin: "0.35rem 0 0", fontSize: "0.82rem", color: "var(--clr-text3)" }}>
            Listado global de todos los usuarios registrados en la plataforma.
          </p>
        </div>
        <button
          onClick={() => setModal({ type: "create" })}
          style={{ padding: "9px 18px", borderRadius: 999, border: "none", background: "var(--clr-org-primary)", color: "#fff", cursor: "pointer", fontSize: "0.875rem", fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}
        >
          + Nuevo usuario
        </button>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          marginBottom: 14, padding: "10px 16px", borderRadius: 12,
          background: "var(--clr-surface)",
          border: "1px solid var(--clr-border)",
          boxShadow: "var(--clr-shadow-sm)",
          color: "var(--clr-text2)",
          fontSize: "0.85rem", fontWeight: 500,
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ width: 7, height: 7, borderRadius: 4, background: toast.ok ? "#22c55e" : "#ef4444", display: "inline-block", flexShrink: 0 }} />
          {toast.msg}
        </div>
      )}

      {/* Search */}
      <input
        type="search"
        placeholder="Buscar por nombre, email, organización o rol…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ width: "100%", padding: "0.5rem 1rem", borderRadius: 999, border: "1px solid var(--clr-border)", background: "var(--clr-input-bg)", color: "var(--clr-text)", fontSize: "0.875rem", outline: "none", boxSizing: "border-box", marginBottom: "1.25rem" }}
      />

      {/* Table */}
      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: "3rem", textAlign: "center", color: "var(--clr-text3)", fontSize: "0.9rem" }}>Cargando usuarios…</div>
        ) : error ? (
          <div style={{ padding: "3rem", textAlign: "center", color: "#ef4444", fontSize: "0.875rem" }}>{error}</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: "3rem", textAlign: "center", color: "var(--clr-text3)", fontSize: "0.9rem" }}>
            {search ? "Sin resultados para esta búsqueda." : "No hay usuarios registrados."}
          </div>
        ) : (
          <div style={{ overflowX: "auto", borderRadius: 18 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={thCell}>Nombre</th>
                  <th style={thCell}>Email</th>
                  <th style={thCell}>Rol</th>
                  <th style={thCell}>Organización</th>
                  <th style={thCell}>Plan</th>
                  <th style={thCell}>Último acceso</th>
                  <th style={thCell}>Estado</th>
                  <th style={{ ...thCell, textAlign: "center" }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => {
                  const name = [u.first_name, u.last_name].filter(Boolean).join(" ") || "—";
                  return (
                    <tr
                      key={`${u.org_id}-${u.id}`}
                      style={{ transition: "background 0.1s" }}
                      onMouseEnter={e => (e.currentTarget.style.background = "var(--clr-hover)")}
                      onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                    >
                      <td style={tdCell}><span style={{ fontWeight: 500 }}>{name}</span></td>
                      <td style={{ ...tdCell, color: "var(--clr-text2)", fontSize: "0.8rem" }}>{u.email}</td>
                      <td style={tdCell}><RoleBadge role={u.role} /></td>
                      <td style={{ ...tdCell, color: "var(--clr-text2)" }}>{u.org_name}</td>
                      <td style={tdCell}><PlanBadge plan={u.org_plan} /></td>
                      <td style={{ ...tdCell, color: "var(--clr-text3)", fontSize: "0.8rem" }}>{formatDate(u.last_login_at)}</td>
                      <td style={tdCell}><StatusDot active={u.is_active} /></td>
                      <td style={{ ...tdCell, textAlign: "center" }}>
                        <ActionsMenu
                          user={u}
                          onEdit={() => setModal({ type: "edit", user: u })}
                          onResetPassword={() => setModal({ type: "resetPassword", user: u })}
                          onToggle={() => toggleActive(u)}
                          onSubstitute={() => setModal({ type: "substitute", user: u })}
                          onDelete={() => setModal({ type: "delete", user: u })}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
